import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Pencil, Trash2, Clock, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { formatTimeRange12h } from '../../utils/timeFormat';
import TimeInput12h from '../ui/time-input-12h';

const emptyForm = () => ({
  schedule_name: '',
  start_time: '08:00',
  end_time: '14:00',
  applies_to_all_classes: false,
  class_ids: [],
});

const SchoolHoursSchedule = ({ classes = [] }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const schoolId = user?.school_id;

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadSchedules = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await api.get('/timetable/class-schedules', { params: { school_id: schoolId } });
      setSchedules(res.data?.data || []);
    } catch (e) {
      toast({
        title: 'Failed to load school hours',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [schoolId, toast]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      schedule_name: row.schedule_name || '',
      start_time: row.start_time || '08:00',
      end_time: row.end_time || '14:00',
      applies_to_all_classes: !!row.applies_to_all_classes,
      class_ids: (row.class_ids || []).map(String),
    });
    setDialogOpen(true);
  };

  const toggleClass = (classId) => {
    const id = String(classId);
    setForm((prev) => {
      const ids = prev.class_ids.map(String);
      if (ids.includes(id)) {
        return { ...prev, class_ids: ids.filter((x) => x !== id) };
      }
      return { ...prev, class_ids: [...ids, id] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!schoolId) return;
    setSaving(true);
    try {
      const payload = {
        school_id: schoolId,
        schedule_name: form.schedule_name.trim() || 'Schedule',
        start_time: form.start_time,
        end_time: form.end_time,
        applies_to_all_classes: form.applies_to_all_classes,
        class_ids: form.applies_to_all_classes ? [] : form.class_ids.map(Number),
      };
      if (editing?.id) {
        await api.put(`/timetable/class-schedules/${editing.id}`, payload);
        toast({ title: 'Schedule updated' });
      } else {
        await api.post('/timetable/class-schedules', payload);
        toast({ title: 'Schedule created' });
      }
      setDialogOpen(false);
      loadSchedules();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err?.response?.data?.message || err?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!schoolId || !row?.id) return;
    if (!window.confirm(`Delete schedule "${row.schedule_name}"?`)) return;
    try {
      await api.delete(`/timetable/class-schedules/${row.id}`, { params: { school_id: schoolId } });
      toast({ title: 'Schedule deleted' });
      loadSchedules();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.message || err?.message || 'Try again',
        variant: 'destructive',
      });
    }
  };

  const classNameById = (id) => classes.find((c) => String(c.id) === String(id))?.name || `Class ${id}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">School hours</h2>
          <p className="text-sm text-muted-foreground">
            Set daily open and close times. Class-specific schedules override the global default.
          </p>
        </div>
        <Button type="button" className="bg-blue-600 hover:bg-blue-700" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Add schedule
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : schedules.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-10 text-center text-muted-foreground">
            No school hour schedules yet. Add a global schedule or assign hours per class.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {schedules.map((row) => (
            <Card key={row.id} className="border-border">
              <CardHeader className="py-3 flex flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <CardTitle className="text-base text-foreground truncate">{row.schedule_name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {formatTimeRange12h(row.start_time, row.end_time)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {row.applies_to_all_classes ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/15 text-green-700 dark:text-green-300">Global</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                      {row.class_count || 0} class(es)
                    </span>
                  )}
                  <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(row)} aria-label="Edit">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="text-red-400" onClick={() => handleDelete(row)} aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              {!row.applies_to_all_classes && (row.class_ids || []).length > 0 && (
                <CardContent className="pt-0 pb-3">
                  <p className="text-xs text-muted-foreground">
                    Classes: {(row.class_ids || []).map(classNameById).join(', ')}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-border text-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit school hours' : 'Add school hours'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Schedule name *</Label>
              <Input
                value={form.schedule_name}
                onChange={(e) => setForm({ ...form, schedule_name: e.target.value })}
                className="bg-muted border-border"
                placeholder="e.g. Primary, Global"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start time *</Label>
                <TimeInput12h
                  value={form.start_time}
                  onChange={(v) => setForm({ ...form, start_time: v })}
                  triggerClassName="bg-muted border-border"
                />
              </div>
              <div>
                <Label>End time *</Label>
                <TimeInput12h
                  value={form.end_time}
                  onChange={(v) => setForm({ ...form, end_time: v })}
                  triggerClassName="bg-muted border-border"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <Label className="text-foreground">Apply to all classes</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Global schedule applies to classes without their own schedule.
                </p>
              </div>
              <Switch
                checked={!!form.applies_to_all_classes}
                onCheckedChange={(v) => setForm({ ...form, applies_to_all_classes: v, class_ids: v ? [] : form.class_ids })}
              />
            </div>
            {!form.applies_to_all_classes && (
              <div>
                <Label>Assign classes</Label>
                {classes.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">No classes available.</p>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto rounded-md border border-border p-2">
                    {classes.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          className="accent-blue-500"
                          checked={form.class_ids.map(String).includes(String(c.id))}
                          onChange={() => toggleClass(c.id)}
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create schedule'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolHoursSchedule;
